import React, { useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import { css } from '@emotion/react';

// Use a global object to store previous overflow values and number of instances,
// since several modals can be open at the same time, or near at the same time,
// because a modal can be open when another one is still closing, due to
// "modal-open" events defined in Modal.
const previousOverflowValue = {};
const numberOfInstances = {};

export const Overlay = ({
  color = 'rgba(34, 34, 34, 0.9)',
  avoidScrollSelector = 'body',
  full = true,
  minHeight = 1,
  maxHeight = 200,
  border = 'none',
  children
}) => {
  // Use useLayoutEffect to ensure this logic is synchronously applied to each instance
  // of this component.
  useLayoutEffect(() => {
    numberOfInstances[avoidScrollSelector] =
      (numberOfInstances[avoidScrollSelector] || 0) + 1;
    // Set "overflow: hidden" on the element that must have scroll disabled.
    if (full && avoidScrollSelector) {
      // Save the previous overflow value only while rendering the first instance.
      if (numberOfInstances[avoidScrollSelector] === 1) {
        previousOverflowValue[avoidScrollSelector] =
          document.querySelector(avoidScrollSelector).style.overflow;
      }
      document.querySelector(avoidScrollSelector).style.overflow = 'hidden';
    }
    return () => {
      // Restore overflow on unmount, only while unmounting the first instance.
      if (
        full &&
        avoidScrollSelector &&
        numberOfInstances[avoidScrollSelector] === 1
      ) {
        document.querySelector(avoidScrollSelector).style.overflow =
          previousOverflowValue[avoidScrollSelector] || '';
      }
      numberOfInstances[avoidScrollSelector] -= 1;
    };
  }, []);

  const styles = {
    overlay: css`
      z-index: 1;
      position: ${full ? 'fixed' : 'absolute'};
      top: 0;
      left: 0;
      right: 0;
      width: 100%;
      height: ${full ? '100vh' : 'auto'};
      min-height: ${full ? 'auto' : `${minHeight}px`};
      max-height: ${full ? 'auto' : `${maxHeight}px`};
      border: ${border || 'none'};
      overflow: auto;
      background-color: ${color};
    `
  };

  return <div css={styles.overlay}>{children}</div>;
};

Overlay.propTypes = {
  color: PropTypes.string,
  avoidScrollSelector: PropTypes.string,
  full: PropTypes.bool,
  minHeight: PropTypes.number,
  maxHeight: PropTypes.number,
  border: PropTypes.string,
  children: PropTypes.node.isRequired
};
